import { useCallback, useEffect, useRef, useState } from "react";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-transcribe`;
const MAX_MS = 60_000;

export type VoiceState = "idle" | "recording" | "transcribing";
export type VoiceError = "micDenied" | "micMissing" | "micUnsupported" | "voiceFailed" | "errorBusy";

export function useVoiceInput(lang: string, onText: (text: string) => void, onError: (e: VoiceError) => void) {
  const [state, setState] = useState<VoiceState>("idle");
  const [level, setLevel] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>();
  const timerRef = useRef<number>();
  const abortRef = useRef<AbortController | null>(null);
  const cancelledRef = useRef(false);

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    ctxRef.current?.close().catch(() => {});
    streamRef.current = null; ctxRef.current = null; setLevel(0);
  };

  const transcribe = async (blob: Blob) => {
    setState("transcribing");
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "speech.webm", { type: blob.type.replace(/^video\//, "audio/") || "audio/webm" }));
      fd.append("lang", lang);
      const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(ENDPOINT, { method: "POST", headers: { apikey: key, Authorization: `Bearer ${key}` }, body: fd, signal: ctrl.signal });
      if (!res.ok || !res.body) throw new Error(res.status === 429 ? "errorBusy" : "voiceFailed");
      const reader = res.body.getReader(); const dec = new TextDecoder();
      let buf = "", acc = "", final = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (!data || data === "[DONE]") continue;
          try {
            const ev = JSON.parse(data);
            if (ev.type === "transcript.text.delta") acc += ev.delta || "";
            else if (ev.type === "transcript.text.done") final = ev.text || "";
            else if (ev.type === "error" || ev.error) throw new Error("voiceFailed");
          } catch (e) { if ((e as Error).message === "voiceFailed") throw e; }
        }
      }
      const text = (final || acc).trim();
      if (!text) throw new Error("voiceFailed");
      onText(text);
    } catch (e: any) {
      if (e?.name !== "AbortError") onError(e?.message === "errorBusy" ? "errorBusy" : "voiceFailed");
    } finally {
      abortRef.current = null; setState("idle");
    }
  };

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return onError("micUnsupported");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        cleanup();
        const blob = new Blob(chunks, { type: (rec.mimeType || "audio/webm").split(";")[0].replace(/^video\//, "audio/") });
        if (cancelledRef.current || blob.size < 1000) { setState("idle"); return; }
        transcribe(blob);
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
      timerRef.current = window.setTimeout(() => rec.state === "recording" && rec.stop(), MAX_MS);
      try {
        const ctx = new AudioContext(); ctxRef.current = ctx;
        const an = ctx.createAnalyser(); an.fftSize = 256;
        ctx.createMediaStreamSource(stream).connect(an);
        const arr = new Uint8Array(an.frequencyBinCount);
        const tick = () => { an.getByteFrequencyData(arr); setLevel(arr.reduce((a, b) => a + b, 0) / arr.length / 128); rafRef.current = requestAnimationFrame(tick); };
        tick();
      } catch { /* visual only */ }
    } catch (e: any) {
      cleanup(); setState("idle");
      onError(e?.name === "NotFoundError" || e?.name === "OverconstrainedError" ? "micMissing" : e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "micDenied" : "voiceFailed");
    }
  }, [lang, onError]);

  const stop = useCallback(() => { if (recRef.current?.state === "recording") recRef.current.stop(); }, []);
  const cancel = useCallback(() => {
    cancelledRef.current = true; abortRef.current?.abort();
    if (recRef.current?.state === "recording") recRef.current.stop(); else cleanup();
  }, []);

  useEffect(() => () => { cancelledRef.current = true; abortRef.current?.abort(); recRef.current?.state === "recording" && recRef.current.stop(); cleanup(); }, []);

  return { state, level, start, stop, cancel };
}
